import { readFunnelAttribution, readPostHogDistinctId } from "@/lib/funnel-planner/attribution";
import {
  computeActualVsPlan,
  computeCapacityBottleneck,
  computeFunnelVolumeMetrics,
  computeMonthlyExpenseTotal,
  computePnL,
  getTemplate,
  scenarioProjection,
} from "@/lib/funnel-planner/engine";
import { summarizeIntegrityWarnings, validateFunnelGraph } from "@/lib/funnel-planner/integrity";
import type { FunnelWizardState } from "@/lib/funnel-planner/schema";
import { toPersistablePlannerState } from "@/lib/funnel-planner/session-fields";
import type { FunnelAttributionData } from "@/lib/funnel-planner/attribution";

export const FUNNEL_PLANNER_CRM_SNAPSHOT_SCHEMA_VERSION = "funnel-planner-crm-v1";
export const FUNNEL_PLANNER_TRACKING_SCHEMA_VERSION = "funnel-planner-tracking-v1";

const PERSONA_STORAGE_KEY = "seoh-persona";
const AUDIENCE_STORAGE_KEY = "seoh_audience";
const PERSONA_IDS = new Set(["none", "agency", "franchise", "creator", "adult", "enterprise"]);
const PERSONA_SLUG_TO_ID = new Map([
  ["agencies", "agency"],
  ["franchise", "franchise"],
  ["creators", "creator"],
  ["adult", "adult"],
  ["enterprise", "enterprise"],
]);

export type FunnelCrmSubmissionAcceptance = Readonly<{
  correlation_id: string;
  idempotency_key: string;
}>;

type CrmSubmissionIdentity = FunnelCrmSubmissionAcceptance;

type FunnelCrmAttempt = Readonly<{
  body: string;
  identity: CrmSubmissionIdentity;
}>;

type CachedFunnelCrmAttempt = {
  key: string;
  promise: Promise<FunnelCrmAttempt>;
  result: unknown | null;
  submission: Promise<void> | null;
};

const FUNNEL_CRM_ATTEMPT_CACHE_LIMIT = 8;
const CRM_INTAKE_CLIENT_DEADLINE_MS = 45_000;
const cachedFunnelCrmAttempts = new Map<string, CachedFunnelCrmAttempt>();

function bytesToLowerHex(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function createCrmSubmissionIdentity(): Promise<CrmSubmissionIdentity> {
  const cryptoApi = globalThis.crypto;

  if (!cryptoApi?.getRandomValues || !cryptoApi.subtle?.digest) {
    throw new Error("browser_crypto_unavailable");
  }

  const entropy = cryptoApi.getRandomValues(new Uint8Array(48));
  const digest = new Uint8Array(await cryptoApi.subtle.digest("SHA-256", entropy));

  return Object.freeze({
    correlation_id: `corr_${bytesToLowerHex(entropy.slice(0, 16))}`,
    idempotency_key: `sha256:${bytesToLowerHex(digest)}`,
  });
}

function funnelCrmAttemptKey(state: FunnelWizardState, locale: string): string {
  return JSON.stringify([locale, state]);
}

async function createFunnelCrmAttempt(
  state: FunnelWizardState,
  locale: string,
): Promise<FunnelCrmAttempt> {
  const identityPromise = createCrmSubmissionIdentity();
  const attribution = readFunnelAttribution();
  const [identity, distinctId] = await Promise.all([identityPromise, readPostHogDistinctId()]);
  const payload = buildCrmPayload(state, locale, {
    ...attribution,
    ...(distinctId ? { posthog_distinct_id: distinctId } : {}),
  });
  const consent = payload.consent && typeof payload.consent === "object" && !Array.isArray(payload.consent)
    ? payload.consent as Record<string, unknown>
    : {};

  return Object.freeze({
    body: JSON.stringify({
      ...payload,
      ...identity,
      consent: {
        ...consent,
        consent_timestamp: new Date().toISOString(),
      },
    }),
    identity,
  });
}

function getOrCreateFunnelCrmAttempt(
  state: FunnelWizardState,
  locale: string,
): CachedFunnelCrmAttempt {
  const key = funnelCrmAttemptKey(state, locale);
  const cachedAttempt = cachedFunnelCrmAttempts.get(key);

  if (cachedAttempt) {
    cachedFunnelCrmAttempts.delete(key);
    cachedFunnelCrmAttempts.set(key, cachedAttempt);
    return cachedAttempt;
  }

  const entry: CachedFunnelCrmAttempt = {
    key,
    promise: createFunnelCrmAttempt(state, locale),
    result: null,
    submission: null,
  };
  cachedFunnelCrmAttempts.set(key, entry);
  pruneFunnelCrmAttempts();
  entry.promise.catch(() => {
    if (cachedFunnelCrmAttempts.get(key) === entry) {
      cachedFunnelCrmAttempts.delete(key);
    }
  });
  return entry;
}

function pruneFunnelCrmAttempts(): void {
  while (cachedFunnelCrmAttempts.size > FUNNEL_CRM_ATTEMPT_CACHE_LIMIT) {
    const removable = Array.from(cachedFunnelCrmAttempts.entries())
      .find(([, entry]) => entry.submission === null);

    if (!removable) {
      return;
    }

    cachedFunnelCrmAttempts.delete(removable[0]);
  }
}

function isDurableCrmQueuedSuccess(body: unknown): boolean {
  if (!body || typeof body !== "object") return false;
  const response = body as Record<string, unknown>;
  return response.ok === true && response.activepieces_delivery === "queued";
}

function crmQueuedResponseMatchesAttempt(
  body: unknown,
  identity: CrmSubmissionIdentity,
): boolean {
  if (!body || typeof body !== "object") return false;
  const response = body as Record<string, unknown>;
  return response.correlation_id === identity.correlation_id &&
    response.idempotency_key === identity.idempotency_key;
}

async function submitFunnelCrmAttempt(attempt: FunnelCrmAttempt): Promise<unknown> {
  const controller = new AbortController();
  const deadlineTimer = globalThis.setTimeout(() => {
    controller.abort();
  }, CRM_INTAKE_CLIENT_DEADLINE_MS);

  try {
    const res = await fetch("/api/crm/intake", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: attempt.body,
      keepalive: attempt.body.length < 60_000,
      signal: controller.signal,
    });
    const responseBody = await res.json().catch(() => null);

    if (!crmQueuedResponseMatchesAttempt(responseBody, attempt.identity)) {
      throw new Error(`crm_${res.status}_identity_mismatch`);
    }
    if (!res.ok) throw new Error(`crm_${res.status}`);
    if (!isDurableCrmQueuedSuccess(responseBody)) {
      throw new Error(`crm_${res.status}_not_queued`);
    }

    return responseBody;
  } catch (error) {
    if (controller.signal.aborted) {
      throw new Error("crm_client_deadline_exceeded");
    }

    throw error;
  } finally {
    globalThis.clearTimeout(deadlineTimer);
  }
}

function countFinalFields(value: unknown): number {
  if (value == null) return 0;
  if (Array.isArray(value)) {
    return value.reduce((total, item) => total + countFinalFields(item), 0);
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    return Object.values(record).reduce<number>((total, item) => total + countFinalFields(item), 0);
  }
  return 1;
}

function numberBand(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "zero";
  if (value < 1_000) return "lt_1k";
  if (value < 10_000) return "1k_10k";
  if (value < 50_000) return "10k_50k";
  if (value < 100_000) return "50k_100k";
  if (value < 500_000) return "100k_500k";
  return "500k_plus";
}

function countActualRows(state: FunnelWizardState): number {
  return state.stages.filter((stage) => stage.actualVolume != null || stage.actualCvr != null || stage.actualPeriodStart || stage.actualPeriodEnd).length;
}

function countAssetRows(state: FunnelWizardState): number {
  return state.stages.filter((stage) => (
    Boolean(stage.assetUrl || stage.assetThumbnailUrl || stage.assetCtaNotes || stage.owner) ||
    Boolean(stage.assetType || stage.assetSourceSystem) ||
    (stage.assetExecutionStatus != null && stage.assetExecutionStatus !== "unassigned")
  )).length;
}

function projectedRevenueAtMonth(state: FunnelWizardState, projectionMonths: number): number {
  const projection = scenarioProjection(
    state.stages,
    { ...state.projectionInputs, projectionMonths },
    1,
    state.trafficChannels,
    state.graph,
  );
  return projection.points.at(-1)?.revenue ?? 0;
}

export function buildFunnelPlannerCrmSnapshot(state: FunnelWizardState): Record<string, unknown> {
  const volumeMetrics = computeFunnelVolumeMetrics(state.stages, state.projectionInputs, state.trafficChannels, state.graph);
  const projectionMonths = state.projectionInputs.projectionMonths;
  const horizonRevenue = projectedRevenueAtMonth(state, projectionMonths);
  const month12Revenue = projectedRevenueAtMonth(state, 12);
  const monthlyExpenseTotal = computeMonthlyExpenseTotal(state.expenses);
  const pnl = state.products.length > 0
    ? computePnL(state.products, state.stages, state.trafficChannels, state.projectionInputs, 0, state.regionalSettings, state.graph, state.expenses)
    : null;
  const modelHealthWarnings = validateFunnelGraph(state);
  const modelHealthSummary = summarizeIntegrityWarnings(modelHealthWarnings);
  const actualRows = computeActualVsPlan(state.stages, state.projectionInputs, state.trafficChannels, state.graph);
  const actualStatusCounts = actualRows.reduce<Record<string, number>>((counts, row) => {
    counts[row.status] = (counts[row.status] ?? 0) + 1;
    return counts;
  }, {});
  const capacity = computeCapacityBottleneck(state);

  return {
    schema_version: FUNNEL_PLANNER_CRM_SNAPSHOT_SCHEMA_VERSION,
    route_template_id: "funnel-planner",
    planner_state: {
      version: state.version,
      step: state.step,
      template_id: state.templateId,
      audience: state.audience,
      goal: state.goal,
      regional_settings: state.regionalSettings,
      audience_goal_details: state.audienceGoalDetails,
      traffic_channels: state.trafficChannels,
      stages: state.stages,
      graph: state.graph ?? null,
      products: state.products,
      capacity_config: state.capacityConfig ?? null,
      projection_inputs: state.projectionInputs,
      expenses: state.expenses,
      scenarios: state.scenarios,
      experiments: state.experiments,
      fit_quiz_band: state.fitQuizBand,
      started_at: state.startedAt,
      completed_at: state.completedAt,
    },
    calculation_summary: {
      total_visitors: volumeMetrics.visitors,
      leads: volumeMetrics.leads,
      qualified_leads: volumeMetrics.stageVolumes[1] ?? null,
      booked_calls: volumeMetrics.stageVolumes[2] ?? null,
      customers: volumeMetrics.customers,
      stage_volumes: volumeMetrics.stageVolumes,
      month12_revenue: month12Revenue,
      projection_months: projectionMonths,
      horizon_revenue: horizonRevenue,
      gross_revenue: pnl?.grossRevenue ?? null,
      net_revenue: pnl?.netRevenue ?? null,
      net_profit: pnl?.netProfit ?? null,
      monthly_expense_total: monthlyExpenseTotal,
      capacity_bottleneck: capacity.bottleneck,
      capacity_calls: capacity.capacityCalls,
      projected_calls: capacity.projectedCalls,
      actual_status_counts: actualStatusCounts,
      model_health_warning_count: modelHealthWarnings.length,
      model_health_warning_categories: Object.keys(modelHealthSummary).sort(),
    },
  };
}

export function buildFunnelPlannerTrackingSummary(state: FunnelWizardState): Record<string, string | number | boolean> {
  const snapshot = buildFunnelPlannerCrmSnapshot(state);
  const volumeMetrics = computeFunnelVolumeMetrics(state.stages, state.projectionInputs, state.trafficChannels, state.graph);
  const projectionMonths = state.projectionInputs.projectionMonths;
  const month12Revenue = projectedRevenueAtMonth(state, 12);
  const horizonRevenue = projectedRevenueAtMonth(state, projectionMonths);
  const modelHealthWarnings = validateFunnelGraph(state);
  const modelHealthSummary = summarizeIntegrityWarnings(modelHealthWarnings);
  const actualRows = countActualRows(state);
  const assetRows = countAssetRows(state);
  const monthlyExpenseTotal = computeMonthlyExpenseTotal(state.expenses);

  return {
    planner_tracking_schema_version: FUNNEL_PLANNER_TRACKING_SCHEMA_VERSION,
    planner_state_version: state.version,
    crm_snapshot_schema_version: FUNNEL_PLANNER_CRM_SNAPSHOT_SCHEMA_VERSION,
    final_field_count: countFinalFields(snapshot),
    field_group_count: Object.keys(snapshot).length,
    traffic_channel_count: state.trafficChannels.length,
    product_count: state.products.length,
    stage_count: state.stages.length,
    graph_node_count: state.graph?.nodes.length ?? 0,
    graph_edge_count: state.graph?.edges.length ?? 0,
    expense_count: state.expenses.length,
    scenario_count: state.scenarios.length,
    experiment_count: state.experiments.length,
    actual_row_count: actualRows,
    asset_count: assetRows,
    tracking_configured_count: state.stages.filter((stage) => stage.trackingStatus && stage.trackingStatus !== "not_set").length,
    has_graph: Boolean(state.graph?.nodes.length),
    has_actuals: actualRows > 0,
    has_expenses: state.expenses.length > 0 || monthlyExpenseTotal > 0,
    has_assets: assetRows > 0,
    has_capacity_cap: state.capacityConfig != null,
    has_custom_scenarios: state.scenarios.some((scenario) => scenario.kind === "custom"),
    has_experiments: state.experiments.length > 0,
    has_reinvestment: (state.projectionInputs.reinvestmentPercent ?? 0) > 0,
    has_model_health_warnings: modelHealthWarnings.length > 0,
    model_health_warning_count: modelHealthWarnings.length,
    model_health_warning_category_count: Object.keys(modelHealthSummary).length,
    visitor_band: numberBand(volumeMetrics.visitors),
    customer_band: numberBand(volumeMetrics.customers),
    monthly_revenue_band: numberBand(month12Revenue),
    projection_months: projectionMonths,
    horizon_revenue_band: numberBand(horizonRevenue),
    monthly_expense_band: numberBand(monthlyExpenseTotal),
  };
}

function hasPreferenceStorageConsent(): boolean {
  if (typeof window === "undefined") return false;
  const consentApi = (window as typeof window & { __seohConsent?: { has?: (category: string) => boolean } }).__seohConsent;

  try {
    return consentApi?.has?.("preferences") === true;
  } catch {
    // See `hasAnalyticsConsent`: an unreadable consent state is "not granted".
    return false;
  }
}

/**
 * Analytics consent, read only from the site-wide consent control the visitor
 * actually operated (the cookie banner's `analytics` category). It is never
 * assumed, and never inherited from the privacy notice or any other checkbox.
 * Absent or undecided consent is `false`.
 */
function hasAnalyticsConsent(): boolean {
  if (typeof window === "undefined") return false;
  const consentApi = (window as typeof window & { __seohConsent?: { has?: (category: string) => boolean } }).__seohConsent;

  try {
    return consentApi?.has?.("analytics") === true;
  } catch {
    // The consent runtime reads localStorage, which throws in some privacy
    // modes. An unreadable consent state is "not granted", not a reason to
    // abandon the visitor's submit.
    return false;
  }
}

function safePathToken(value: string): string | undefined {
  const normalized = value
    .split(/[?#]/, 1)[0]
    .replace(/[^a-zA-Z0-9_./:-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/\/{2,}/g, "/")
    .replace(/^-|-$/g, "")
    .slice(0, 160);

  return normalized && !normalized.includes("@") ? normalized : undefined;
}

function readStoredValue(key: string): string {
  if (typeof window === "undefined" || !hasPreferenceStorageConsent()) return "";
  try {
    return window.localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function readResolvedPersona(): string {
  if (typeof document === "undefined") return "none";
  const documentPersona = (document.documentElement.getAttribute("data-persona") || "").trim().toLowerCase();
  if (PERSONA_IDS.has(documentPersona)) return documentPersona;

  const storedPersona = readStoredValue(PERSONA_STORAGE_KEY).trim().toLowerCase();
  if (PERSONA_IDS.has(storedPersona)) return storedPersona;

  if (typeof window !== "undefined") {
    const firstSegment = window.location.pathname.split("/").filter(Boolean)[1] || window.location.pathname.split("/").filter(Boolean)[0] || "";
    const personaFromPath = PERSONA_SLUG_TO_ID.get(firstSegment.toLowerCase());
    if (personaFromPath) return personaFromPath;
  }

  return "none";
}

function readAudiencePath(state: FunnelWizardState): string | undefined {
  const storedAudience = safePathToken(readStoredValue(AUDIENCE_STORAGE_KEY));
  if (storedAudience) return `audience:${storedAudience}`;

  if (state.audience) {
    return `audience:${state.audience}`;
  }

  if (typeof window !== "undefined") {
    return safePathToken(window.location.pathname || "/funnel-planner");
  }

  return undefined;
}

export function buildCrmPayload(
  state: FunnelWizardState,
  locale: string,
  attribution: FunnelAttributionData,
): Record<string, unknown> {
  const isAdultPlannerContext = state.templateId === "adult-compliance" || state.audience === "adult";

  return {
    email:   state.contact!.email,
    name:    state.contact!.name,
    company: state.contact!.company,

    source_page:        "/funnel-planner",
    source_locale:      locale,
    form_variant:       "funnel-planner-export",
    cta_location:       "funnel-planner-step6",
    route_template_id:  "funnel-planner",
    source_template:    "components/funnel-planner/steps/ContactCaptureStep.tsx",

    service_line:       state.templateId ? getTemplate(state.templateId).serviceLine : undefined,
    service_interest:   state.templateId,
    buyer_segment:      state.audience,
    persona:            readResolvedPersona(),
    recommended_band:   state.fitQuizBand ?? undefined,
    audience_path:      readAudiencePath(state),
    funnel_stage:       "consideration",
    automation_purpose: ["LEAD_MAGNET"],
    content_theme:      "funnel-planner",
    funnel_planner:     buildFunnelPlannerCrmSnapshot(state),

    ...attribution,

    ...(isAdultPlannerContext ? {
      adult_context: {
        adult_selected_service: true,
        ...(state.templateId === "adult-compliance" ? { adult_selected_service_slug: "adult-compliance" } : {}),
      },
    } : {}),

    // Each consent field is derived from its own explicit control and from
    // nothing else. Accepting the privacy notice is a precondition for handling
    // the plan; it is not evidence of marketing, analytics, or messaging
    // consent, and inferring any of those from it was the defect this replaces.
    // Every optional marketing/messaging consent defaults to false, and the
    // three channels this funnel collects no control for are pinned false
    // rather than omitted, so the independence is visible in the payload.
    consent: {
      privacy_notice_accepted: state.contact!.privacyNoticeAccepted === true,
      analytics_consent:       hasAnalyticsConsent(),
      marketing_opt_in:        state.contact!.marketingEmailOptIn === true,
      email_marketing_consent: state.contact!.marketingEmailOptIn === true,
      sms_consent:             false,
      whatsapp_consent:        false,
      push_consent:            false,
      consent_locale:          locale,
    },

    anti_spam: {
      honeypot:           "",
      submitted_after_ms: Date.now() - new Date(state.startedAt).getTime(),
    },
  };
}

export async function submitFunnelCrm(
  state: FunnelWizardState,
  locale: string,
): Promise<FunnelCrmSubmissionAcceptance> {
  const attemptEntry = getOrCreateFunnelCrmAttempt(state, locale);

  if (attemptEntry.result) {
    const attempt = await attemptEntry.promise;

    if (
      isDurableCrmQueuedSuccess(attemptEntry.result) &&
      crmQueuedResponseMatchesAttempt(attemptEntry.result, attempt.identity)
    ) {
      return attempt.identity;
    }

    attemptEntry.result = null;
  }

  if (attemptEntry.submission) {
    await attemptEntry.submission;
    return (await attemptEntry.promise).identity;
  }

  const submission = (async () => {
    const attempt = await attemptEntry.promise;
    const responseBody = await submitFunnelCrmAttempt(attempt);

    attemptEntry.result = responseBody;
  })();
  attemptEntry.submission = submission;

  try {
    await submission;
    return (await attemptEntry.promise).identity;
  } finally {
    if (attemptEntry.submission === submission) {
      attemptEntry.submission = null;
    }
    pruneFunnelCrmAttempts();
  }
}

export async function fetchFunnelPdf(
  state: FunnelWizardState,
  locale: string,
  options?: { signal?: AbortSignal },
): Promise<Blob> {
  const body = JSON.stringify({
    // The PDF document has no v7 surface; the session-only fields are dropped
    // so the request payload stays identical in shape to the v6 contract.
    wizardState: toPersistablePlannerState(state),
    locale,
    generatedAt: new Date().toISOString(),
  });

  const res = await fetch("/api/funnel-planner/pdf", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    signal: options?.signal,
  });

  if (!res.ok) {
    const json = await res.json().catch(() => ({})) as { code?: string };
    throw new Error(json.code ?? `pdf_${res.status}`);
  }

  return res.blob();
}

export function triggerPdfDownload(blob: Blob, templateId: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `seoh-funnel-plan-${templateId}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
